import React, { ChangeEvent, useContext, useMemo, useCallback } from 'react';
import { Checkbox } from '@core-ui/checkbox';
import { Radio } from '@core-ui/radio';
// @ts-ignore
import { RailContext } from '@cat/rail';
import { ColorSwatch } from '@core-ui/color-swatch';
import { BreakpointContext, MEDIUM } from '@core-ui/breakpoint-provider';
import { BrandInfoContext } from '@core-ui/brand-info-provider';
import { isMobile } from 'react-device-detect';
import { CSSObject, useTheme, forBrands } from '@core-ui/react-stitch';
import { FeatureFlagsContext } from '@core-ui/feature-flags';
// @ts-ignore
import { StarRatingFacet } from '../StarRatingFacet/StarRatingFacet';
import { Option, FacetItemProps } from '../../../types';
import {
  customBackgroundColorForRadio,
  customRadioStyles,
  displayItemStyle,
  displayWrapperStyleForBananaRepublic,
  displayWrapperStylesForAllBrands,
} from './collaborators/getFacetItemStyles';

const getBackground = (color: string) => {
  switch (color.toLowerCase()) {
    case 'multi':
      return 'linear-gradient(to right, red, orange, yellow, green, blue, purple)';
    default:
      return color.toLowerCase();
  }
};

// eslint-disable-next-line complexity
const FacetItem = ({
  facetDisplay,
  applied,
  option,
  facetName,
}: FacetItemProps) => {
  const { onFacetChange } = useContext(RailContext);
  const { minWidth } = useContext(BreakpointContext);
  const theme = useTheme();
  const { enabledFeatures } = useContext(FeatureFlagsContext);
  const isBRcolor2023FFEnabled = enabledFeatures['br-colors-2023'];
  const { abbrBrand } = useContext(BrandInfoContext);
  const isBR = abbrBrand === 'br' || abbrBrand === 'brfs';

  const displayWrapperStyle = (isBRcolor2023FFEnabled?:boolean): CSSObject => ({
    textTransform: 'capitalize',
    ...(minWidth(MEDIUM) ? {} : { paddingLeft: '1rem', paddingRight: '1rem' }),
    ...(forBrands(theme, {
      br: displayWrapperStyleForBananaRepublic(isBRcolor2023FFEnabled),
      brfs: displayWrapperStyleForBananaRepublic(isBRcolor2023FFEnabled),
      default: displayWrapperStylesForAllBrands,
      
    }) as CSSObject),
  });

  const onChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      onFacetChange &&
        onFacetChange({
          facetName,
          id: option.id,
          name: option.name,
          applied: event.currentTarget.checked === true,
        });
    },
    [facetName, onFacetChange, option]
  );

  const shouldEnableInternalLinkURL = () =>
    enabledFeatures['seo-internal-link'] &&
    ['style', 'color'].includes(facetName);

  const createInternalLinkURL = () => {
    if (!shouldEnableInternalLinkURL()) {
      return undefined;
    }
    const { origin, pathname, search } = window.location;
    const searchParams = new URLSearchParams(search);

    if (facetName === 'color') {
      searchParams.set(facetName, option.value);
    } else {
      searchParams.set(facetName, option.id);
    }

    return new URL(`${origin}${pathname}?${searchParams}`);
  };

  const internalLink = createInternalLinkURL();

  const MemoizedStarRatingFacet = useMemo(
    () => (
      <StarRatingFacet
        applied={applied}
        facetName={facetName}
        isMobile={isMobile}
        name={option.name}
        onChange={onChange}
        value={option.id}
      />
    ),
    [applied, facetName, option, onChange]
  );

  switch (facetDisplay) {
    case 'checkbox':
      return (
        <div css={displayWrapperStyle(isBRcolor2023FFEnabled)}>
          <Checkbox
            isChecked={applied}
            labelText={option.name}
            onChange={onChange}
            url={internalLink}
          />
        </div>
      );
    case 'radio':
      return (
        <div css={displayWrapperStyle()}>
          <Radio
            checked={applied}
            customBackgroundColor={customBackgroundColorForRadio(theme,isBRcolor2023FFEnabled)}
            id={option.id}
            isRadioSmall={isBR}
            label={option.name}
            name={facetName}
            onChange={onChange}
            styles={customRadioStyles(theme,isBRcolor2023FFEnabled)}
          />
        </div>
      );
    case 'ratings':
      return MemoizedStarRatingFacet;
    case 'swatch':
      return (
        <div css={displayItemStyle(theme,isBRcolor2023FFEnabled)}>
          <ColorSwatch
            className="facetbar-item-colorswatch"
            colorName={option.name}
            customColor={getBackground(option.value)}
            id={option.id}
            link={internalLink}
            onChange={onChange}
            selected={applied}
            showColorName
            type="checkbox"
            url=""
            value={option.value}
          />
        </div>
      );
    default:
      return null;
  }
};

export default FacetItem;
